import React from 'react';
import Button from '../Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { LoginResponseData, StripeProductDto } from '@EHSNavigator/backend';
import clsx from 'clsx';
import { useLocalStorage } from '@uidotdev/usehooks';

interface PricingPlansCardProps {
  handleClickFreeTrial?: () => void;
  handleClickPayStripe?: () => void;
  data: StripeProductDto;
  subscriptionModal?: boolean;
  handlePlanClick?: (e: any) => void;
  disable?: { other: boolean; upgrade: boolean };
  loading?: { other: boolean; upgrade: boolean };
  otherClasses?: string;
  mode?: 'light' | 'dark';
  type?: string;
}

const PricingPlansCard: React.FC<PricingPlansCardProps> = ({
  data,
  handleClickFreeTrial,
  handleClickPayStripe,
  subscriptionModal,
  loading,
  handlePlanClick,
  disable,
  otherClasses,
  mode,
  type,
}) => {
  const sizes = useButtonSizeLarge();
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );

  return data?.metadata?.plan === 'growth' || mode === 'dark' ? (
    <div
      className={clsx('flex h-fit flex-col items-center gap-6', otherClasses)}
      data-testid="pricing-plans-card"
    >
      <div
        className={clsx(
          'bg-brand-byzantine rounded-10 shadow-pricing-plans-card w-full',
          subscriptionModal ? 'p-4' : 'p-22 lg:p-9',
        )}
      >
        <div className="max-w-191">
          {subscriptionModal ? (
            <h4 className="text-dark-neutral-tones-text capitalize">
              {data?.name}
            </h4>
          ) : (
            <h3 className="text-dark-neutral-tones-text capitalize">
              {data?.name}
            </h3>
          )}
          <p className="text-dark-neutral-tones-text font-inter-variable mt-15 text-sm font-normal">
            {data?.description}
          </p>
        </div>
        <hr className="my-5 border-white/50 lg:my-7" />
        <h5
          className={clsx(
            'text-dark-neutral-tones-text font-semibold lg:font-bold',
            subscriptionModal ? 'h2' : 'h1',
          )}
        >
          ${data?.price?.unit_amount / 100}
        </h5>
        <span className="font-inter-variable text-sm font-bold text-white/80">
          {data?.price?.nickname}, Per{' '}
          <span className="inline-block capitalize">
            {data?.price?.recurring?.interval}
          </span>
        </span>
        <hr className="my-5 border-white/50 lg:my-7" />
        <div className="flex flex-col gap-15">
          {subscriptionModal ? (
            <div className="flex flex-col justify-center gap-2">
              {type === 'trialing' ? (
                <Button
                  onClick={() =>
                    handlePlanClick?.({
                      type: 'subscribe',
                    })
                  }
                  variant="primary"
                  otherClasses="border-white"
                  size="smaller"
                  disable={disable?.upgrade}
                  loading={disable?.upgrade}
                >
                  Go Premium
                </Button>
              ) : type === 'past_due' ? (
                <Button
                  onClick={() =>
                    handlePlanClick?.({
                      type: 'past-due',
                    })
                  }
                  variant="primary"
                  otherClasses="border-white"
                  size="smaller"
                  disable={disable?.upgrade}
                  loading={disable?.upgrade}
                >
                  Pay Subscription
                </Button>
              ) : (
                <Button
                  onClick={() =>
                    handlePlanClick?.({
                      newPriceId: data?.default_price,
                      maxCount: data?.metadata?.max_count,
                    })
                  }
                  variant="primary"
                  otherClasses="!border-brand-byzantine"
                  size="smaller"
                  disable={
                    (!userInfo?.currentSubscription?.requiresPaymentModal &&
                      data?.metadata?.plan?.toLowerCase() ===
                        userInfo?.currentSubscription?.name.toLowerCase()) ||
                    disable?.other
                  }
                  loading={loading?.upgrade}
                >
                  {(Number(data?.metadata?.max_count) ===
                    Number(userInfo?.currentSubscription?.max_count) ||
                    data?.metadata?.max_count) ===
                  userInfo?.currentSubscription?.max_count
                    ? `Current Plan`
                    : Number(userInfo?.currentSubscription?.max_count) >
                        Number(data?.metadata?.max_count)
                      ? `Downgrade`
                      : `Upgrade`}
                </Button>
              )}

              {(Number(data?.metadata?.max_count) ===
                Number(userInfo?.currentSubscription?.max_count) ||
                data?.metadata?.max_count) ===
                userInfo?.currentSubscription?.max_count && type !== 'past_due' && (
                <Button
                  onClick={() => {
                    if (type === 'cancelling') {
                      handlePlanClick?.({
                        type: 're-activate-subscription',
                      });
                    } else {
                      handlePlanClick?.({
                        newPriceId: data?.default_price,
                        maxCount: data?.metadata?.max_count,
                        type: 'cancel-subscription',
                      });
                    }
                  }}
                  variant="secondary"
                  otherClasses="!border-brand-byzantine !hover:bg-transparent"
                  size="smaller"
                  disable={disable?.other}
                  loading={loading?.other}
                >
                  {type === 'cancelling'
                    ? `Re Activate Subscription`
                    : `Cancel Subscription`}
                </Button>
              )}
            </div>
          ) : (
            <>
              <Button
                onClick={() => handleClickFreeTrial?.()}
                variant="secondary"
                otherClasses="!border-brand-byzantine"
                size={sizes}
              >
                Start Free 7-day Trial
              </Button>
              <Button
                onClick={() => handleClickPayStripe?.()}
                variant="stripe"
                otherClasses="!border-brand-byzantine"
              >
                Pay with
                <img
                  src="/assets/images/stripe-logo.png"
                  alt="stripe-logo"
                  className="h-[18px] w-11"
                />
              </Button>
            </>
          )}
        </div>
      </div>
      {mode !== 'dark' && (
        <p className="font-inter-variable text-brand-papery text-center text-base font-bold uppercase lg:text-lg">
          RECOMMENDED
        </p>
      )}
    </div>
  ) : (
    <div
      data-testid="pricing-plans-card"
      className={clsx(
        'border-brand-byzantine rounded-10 h-fit w-fit border-[3px] bg-black/5 lg:mt-9',
        subscriptionModal ? 'p-4' : 'p-22 lg:p-9',
        otherClasses,
      )}
    >
      <div className="max-w-191">
        {subscriptionModal ? (
          <h4 className="text-neutral-tones-text"> {data?.name}</h4>
        ) : (
          <h3 className="text-neutral-tones-text"> {data?.name}</h3>
        )}
        <p className="text-neutral-tones-text font-inter-variable mt-15 text-sm font-normal">
          {data?.description}
        </p>
      </div>
      <hr className="border-neutral-tones-text/50 my-5 lg:my-7" />
      <h5
        className={clsx(
          'text-neutral-tones-text font-semibold lg:font-bold',
          subscriptionModal ? 'h2' : 'h1',
        )}
      >
        ${data?.price?.unit_amount / 100}
      </h5>
      <span className="font-inter-variable text-neutral-tones-text/80 text-sm font-bold">
        {data?.price?.nickname}, Per{' '}
        <span className="inline-block capitalize">
          {data?.price?.recurring?.interval}
        </span>
      </span>
      <hr className="border-neutral-tones-text/50 my-5 lg:my-7" />
      <div className="flex flex-col gap-15">
        {subscriptionModal ? (
          <div className="flex flex-col justify-center gap-2">
            {type === 'trialing' ? (
              <Button
                onClick={() =>
                  handlePlanClick?.({
                    type: 'subscribe',
                  })
                }
                variant="primary"
                otherClasses="border-white"
                size="smaller"
                disable={disable?.other}
                loading={loading?.other}
              >
                Subscribe
              </Button>
            ) : type === 'past_due' ? (
              <Button
                onClick={() =>
                  handlePlanClick?.({
                    type: 'past-due',
                  })
                }
                variant="primary"
                otherClasses="border-white"
                size="smaller"
                disable={disable?.other}
                loading={loading?.other}
              >
                Pay Subscription
              </Button>
            ) : (
              <Button
                onClick={() =>
                  handlePlanClick?.({
                    newPriceId: data?.default_price,
                    maxCount: data?.metadata?.max_count,
                  })
                }
                variant="primary"
                otherClasses="!border-brand-byzantine"
                size="smaller"
                disable={
                  (!userInfo?.currentSubscription?.requiresPaymentModal &&
                    data?.metadata?.plan?.toLowerCase() ===
                      userInfo?.currentSubscription?.name.toLowerCase()) ||
                  disable?.other
                }
                loading={loading?.upgrade}
              >
                {(Number(data?.metadata?.max_count) ===
                  Number(userInfo?.currentSubscription?.max_count) ||
                  data?.metadata?.max_count) ===
                userInfo?.currentSubscription?.max_count
                  ? `Current Plan`
                  : Number(userInfo?.currentSubscription?.max_count) >
                      Number(data?.metadata?.max_count)
                    ? `Downgrade`
                    : `Upgrade`}
              </Button>
            )}

            {(Number(data?.metadata?.max_count) ===
              Number(userInfo?.currentSubscription?.max_count) ||
              data?.metadata?.max_count) ===
              userInfo?.currentSubscription?.max_count && (
              <Button
                onClick={() => {
                  if (type === 'cancelling') {
                    handlePlanClick?.({
                      type: 're-activate-subscription',
                    });
                  } else {
                    handlePlanClick?.({
                      newPriceId: data?.default_price,
                      maxCount: data?.metadata?.max_count,
                      type: 'cancel-subscription',
                    });
                  }
                }}
                variant="secondary"
                otherClasses="!border-brand-byzantine"
                size="smaller"
                disable={disable?.other}
                loading={loading?.other}
              >
                {type === 'cancelling'
                  ? `Re Activate Subscription`
                  : `Cancel Subscription`}
              </Button>
            )}
          </div>
        ) : (
          <>
            <Button
              onClick={() => handleClickFreeTrial?.()}
              variant="secondary"
              otherClasses="!border-brand-byzantine"
              size={sizes}
            >
              Start Free 7-day Trial
            </Button>
            <Button
              onClick={() => handleClickPayStripe?.()}
              variant="stripe"
              otherClasses="!border-brand-byzantine"
            >
              Pay with
              <img
                src="/assets/images/stripe-logo.png"
                alt="stripe-logo"
                className="h-[18px] w-11"
              />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PricingPlansCard;
